import { useState, useMemo } from 'react';
import { X } from 'lucide-react';
import type { MoneyFlowEvent } from '@/types/database';
import { formatDate } from '@/lib/dateUtils';

interface AddMoneyFlowModalProps {
  onClose: () => void;
  onSave: (data: {
    event_date: string;
    wise_usd_sent: number;
    fee_usd: number;
    exchange_rate_lkr_usd: number;
    p2p_rate_lkr_usdt: number;
    usdt_sent: number;
    mvr_rate_per_usdt: number;
    notes: string;
  }) => Promise<void>;
  editing?: MoneyFlowEvent | null;
}

const inputCls = 'w-full px-3 py-2 bg-white/10 border border-white/10 rounded-lg text-xs text-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 placeholder:text-white/40';

export default function AddMoneyFlowModal({ onClose, onSave, editing }: AddMoneyFlowModalProps) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    event_date: editing?.event_date || formatDate(new Date()),
    wise_usd_sent: editing ? String(editing.wise_usd_sent) : '',
    fee_usd: editing ? String(editing.fee_usd) : '',
    exchange_rate_lkr_usd: editing ? String(editing.exchange_rate_lkr_usd) : '',
    p2p_rate_lkr_usdt: editing ? String(editing.p2p_rate_lkr_usdt) : '',
    usdt_sent: editing ? String(editing.usdt_sent) : '',
    mvr_rate_per_usdt: editing ? String(editing.mvr_rate_per_usdt) : '',
    notes: editing?.notes || '',
  });

  const wiseUsd = parseFloat(form.wise_usd_sent) || 0;
  const feeUsd = parseFloat(form.fee_usd) || 0;
  const exRate = parseFloat(form.exchange_rate_lkr_usd) || 0;
  const p2pRate = parseFloat(form.p2p_rate_lkr_usdt) || 0;
  const usdtSent = parseFloat(form.usdt_sent) || 0;
  const mvrRate = parseFloat(form.mvr_rate_per_usdt) || 0;

  const finalLkr = useMemo(() => (wiseUsd - feeUsd) * exRate, [wiseUsd, feeUsd, exRate]);
  const usdtBought = useMemo(() => (p2pRate > 0 ? finalLkr / p2pRate : 0), [finalLkr, p2pRate]);
  const finalMvr = useMemo(() => usdtSent * mvrRate, [usdtSent, mvrRate]);

  function update(field: string, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit() {
    if (wiseUsd <= 0) return;
    setSaving(true);
    try {
      await onSave({
        event_date: form.event_date,
        wise_usd_sent: wiseUsd,
        fee_usd: feeUsd,
        exchange_rate_lkr_usd: exRate,
        p2p_rate_lkr_usdt: p2pRate,
        usdt_sent: usdtSent,
        mvr_rate_per_usdt: mvrRate,
        notes: form.notes,
      });
    } catch {
      setSaving(false);
    }
  }

  const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="glass-modal rounded-2xl max-w-2xl w-full">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h2 className="text-sm font-bold text-white">
            {editing ? 'Edit Event' : 'Add Event'}
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-white p-1 glass-hover rounded-lg"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2.5">
              <p className="text-[9px] font-bold text-white uppercase tracking-wider">USD to LKR</p>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[10px] font-semibold text-white mb-0.5">Date</label>
                  <input type="date" value={form.event_date} onChange={e => update('event_date', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-white mb-0.5">Wise USD Sent</label>
                  <input type="number" step="0.01" placeholder="0.00" value={form.wise_usd_sent} onChange={e => update('wise_usd_sent', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-white mb-0.5">Fee USD</label>
                  <input type="number" step="0.01" placeholder="0.00" value={form.fee_usd} onChange={e => update('fee_usd', e.target.value)} className={inputCls} />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-white mb-0.5">Exchange Rate (LKR/USD)</label>
                <input type="number" step="0.01" placeholder="e.g. 298.50" value={form.exchange_rate_lkr_usd} onChange={e => update('exchange_rate_lkr_usd', e.target.value)} className={inputCls} />
              </div>

              {finalLkr > 0 && (
                <div className="bg-teal-500/10 border border-teal-500/20 rounded-lg px-3 py-1.5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[9px] font-medium text-teal-400">Final LKR Received</p>
                      <p className="text-sm font-bold text-teal-300">LKR {fmt(finalLkr)}</p>
                    </div>
                    <p className="text-[9px] text-teal-400">({wiseUsd.toFixed(2)} - {feeUsd.toFixed(2)}) x {exRate.toFixed(2)}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-[10px] font-semibold text-white mb-0.5">P2P Rate (LKR/USDT)</label>
                <input type="number" step="0.01" placeholder="e.g. 295.00" value={form.p2p_rate_lkr_usdt} onChange={e => update('p2p_rate_lkr_usdt', e.target.value)} className={inputCls} />
              </div>

              {usdtBought > 0 && (
                <div className="bg-sky-500/10 border border-sky-500/20 rounded-lg px-3 py-1.5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[9px] font-medium text-sky-400">USDT Bought</p>
                      <p className="text-sm font-bold text-sky-300">{fmt(usdtBought)} USDT</p>
                    </div>
                    <p className="text-[9px] text-sky-400">LKR {fmt(finalLkr)} / {p2pRate.toFixed(2)}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2.5">
              <p className="text-[9px] font-bold text-white uppercase tracking-wider">USDT to MVR</p>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-semibold text-white mb-0.5">USDT Sent</label>
                  <input type="number" step="0.01" placeholder="0.00" value={form.usdt_sent} onChange={e => update('usdt_sent', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-white mb-0.5">MVR Rate per USDT</label>
                  <input type="number" step="0.01" placeholder="e.g. 20.50" value={form.mvr_rate_per_usdt} onChange={e => update('mvr_rate_per_usdt', e.target.value)} className={inputCls} />
                </div>
              </div>

              {finalMvr > 0 && (
                <div className="bg-emerald-500/10 border border-emerald-100 rounded-lg px-3 py-1.5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[9px] font-medium text-emerald-400">Final MVR Out</p>
                      <p className="text-sm font-bold text-emerald-400">MVR {fmt(finalMvr)}</p>
                    </div>
                    <p className="text-[9px] text-emerald-400">{usdtSent.toFixed(2)} USDT x {mvrRate.toFixed(2)}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-[10px] font-semibold text-white mb-0.5">Notes (optional)</label>
                <textarea
                  placeholder="Add notes..."
                  value={form.notes}
                  onChange={e => update('notes', e.target.value)}
                  rows={2}
                  className={`${inputCls} resize-none`}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-xs glass-button text-white rounded-lg font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || wiseUsd <= 0}
            className="px-4 py-1.5 text-xs glass-button-active text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : editing ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
